import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Rating from '@mui/material/Rating';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

// Custom Grid components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

// Shared testimonials data
import { testimonials } from '../data/testimonials';

// Helper function to get initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  // First letter of first name and first letter of last name
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const TestimonialsPage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/testimonials'];
  const structuredData = generateStructuredData('/testimonials');

  return (
    <>
      <SEO 
        pathname="/testimonials" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        pb: { xs: 6, md: 8 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      {/* Decorative background pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
          zIndex: 0,
        }}
      />

      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          opacity: 0.05,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -80,
          right: -80,
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
          opacity: 0.05,
          zIndex: 0,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: { xs: 6, md: 8 },
            mb: { xs: 6, md: 8 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Hero decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, transparent 100%)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 100%)`,
            }}
          />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                Client Testimonials
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  maxWidth: 800,
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  fontWeight: 300,
                  opacity: 0.95
                }}
              >
                Hear from our valued clients about their experience with The United Travel
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, md: 4 }, 
                mb: { xs: 5, md: 6 },
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              <Typography 
                variant="h4" 
                gutterBottom
                sx={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: { xs: '1.75rem', md: '2rem' },
                  mb: 2
                }}
              >
                What Our Clients Say
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{
                  fontSize: { xs: '0.9375rem', md: '1rem' },
                  lineHeight: 1.8,
                }}
              >
                Don't just take our word for it. Our clients consistently praise our service quality, 
                reliability, and attention to detail. Read what they have to say about their experience with 
                The United Travel - from corporate transportation to personal fleet bookings.
              </Typography>
            </Paper>
          </motion.div>

          <GridContainer spacing={4} sx={{ mb: { xs: 4, md: 6 } }}>
            {testimonials.map((testimonial, index) => (
              <GridItem xs={12} md={6} key={testimonial.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{ height: '100%' }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      background: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-6px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    {/* Decorative Quote Icon */}
                    <FormatQuoteIcon
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        fontSize: '5rem',
                        color: alpha(theme.palette.primary.main, 0.06),
                        transform: 'scaleX(-1)',
                        zIndex: 0,
                      }}
                    />

                    {/* Testimonial Content */}
                    <Box sx={{ 
                      position: 'relative', 
                      zIndex: 1, 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column' 
                    }}>
                      <Box sx={{ 
                        pl: { xs: 2, md: 3 }, 
                        mb: 3, 
                        pt: 1,
                        minHeight: '120px'
                      }}>
                        <Typography
                          variant="body1"
                          component="blockquote"
                          sx={{
                            fontStyle: 'italic',
                            fontWeight: 400,
                            lineHeight: 1.8,
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                            color: 'text.primary',
                          }}
                        >
                          {testimonial.testimonial}
                        </Typography>
                      </Box>

                      <Divider 
                        sx={{ 
                          my: 2,
                          borderColor: alpha(theme.palette.divider, 0.3)
                        }} 
                      />

                      {/* Client Info */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1.5, sm: 2 },
                        mt: 'auto'
                      }}>
                        <Avatar
                          sx={{
                            width: { xs: 64, sm: 72, md: 80 },
                            height: { xs: 64, sm: 72, md: 80 },
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                            border: '4px solid white',
                            flexShrink: 0,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(testimonial.name)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                              mb: 0.5,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          {testimonial.isCorporate && testimonial.position && testimonial.company && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                                mb: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {testimonial.position}, {testimonial.company}
                            </Typography>
                          )}
                          <Rating
                            value={testimonial.rating}
                            readOnly
                            size="small"
                            sx={{
                              '& .MuiRating-iconFilled': {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </motion.div>
              </GridItem>
            ))}
          </GridContainer>
        </Container>
      </Box>
    </Box>
    </>
  );
};

export default TestimonialsPage;

